import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, FormInstance, Message, Spin, Steps } from '@arco-design/web-react';

import { usePathTo } from '@/utils/useUrlParams';

import { BreadcrumbNav, BreadcrumbNavItemProps } from '../BreadcrumbNav';
import styles from './index.module.less';

/**
 * 步骤表单底部额外按钮
 */
export interface StepFormExtraButton {
  key: string;
  /** 按钮文案 */
  text: React.ReactNode;
  type?: 'default' | 'primary' | 'secondary' | 'outline' | 'dashed' | 'text';
  status?: 'warning' | 'danger' | 'success';
  /**
   * 是否展示；为函数时接收当前步骤上下文。
   * 默认仅在最后一步展示。
   */
  visible?: boolean | ((ctx: { current: number; total: number; type?: string }) => boolean);
  /** 点击前是否先校验表单，默认 true */
  validate?: boolean;
  onClick: (values: Record<string, any>) => void | Promise<void>;
  /** 包裹按钮节点（如权限组件） */
  wrapper?: (button: React.ReactElement) => React.ReactNode;
}

/**
 * 单个步骤的配置接口
 */
interface StepFormConfig {
  key: string;
  /** 步骤标题，显示在步骤导航中 */
  title: string;
  /** 步骤对应的组件，用于渲染该步骤的表单内容 */
  ele: React.ComponentType<any>;
  /** 是否隐藏该步骤，隐藏的步骤不会显示在导航中但仍会渲染 */
  hidden?: boolean;
  /** 当前步骤激活时合并进 Form 的配置（在全局 formProps 之后合并，style 会深度合并） */
  formProps?: Record<string, any>;
}

/**
 * 步骤表单组件的属性接口
 */
interface StepFormProps {
  /** 面包屑导航项数组，用于显示在页面顶部 */
  breadcrumb?: BreadcrumbNavItemProps[];
  /** 表单标题，显示在页面顶部 */
  title?: string;
  /** 步骤配置数组，定义每个步骤的组件和属性 */
  cmpConfigs: StepFormConfig[];
  /** 表单类型，用于区分不同的表单场景 */
  type?: string;
  /** 是否显示步骤导航条，默认为 true */
  showStep?: boolean;
  /** 加载状态，用于初始化表单数据 */
  loading?: boolean;
  /** 表单默认值，用于初始化表单字段 */
  defaultValues?: Record<string, any>;
  /** 表单配置，用于设置表单的样式和布局 */
  formProps?: any;
  /** 外部表单实例（可选） */
  form?: FormInstance;
  /** 额外的选项参数，传递给子组件使用 */
  options?: Record<string, any>;
  /** 需要展开的参数，传递给子组件使用 */
  expandOptions?: Record<string, any>;
  /** 表单提交回调函数，接收最终的表单数据 */
  onSubmit: (values: Record<string, any>) => void | Promise<void>;
  /** 最后一步主按钮文案，默认「确认」 */
  submitText?: React.ReactNode;
  /** 底部额外按钮（默认仅最后一步展示，位于主提交按钮前） */
  extraButtons?: StepFormExtraButton[];
  /** 包裹最后一步主提交按钮（如权限组件） */
  wrapSubmitButton?: (button: React.ReactElement) => React.ReactNode;
  /** 取消操作回调函数，可选，默认执行返回上一页 */
  onCancel?: () => void;
  /** 下一步操作回调函数，接收当前步骤的索引和表单数据 */
  onNext?: (step: number, values: Record<string, any>) => void | Promise<void>;
  /** 上一步操作回调函数，接收当前步骤的索引 */
  onPrevious?: (step: number) => void;
}

/**
 * 传递给子组件的公共属性接口
 */
interface CommonProps {
  /** 表单实例，用于表单操作和验证 */
  form: any;
  /** 默认验证规则数组 */
  defaultRules: any[];
  /** 初始化数据，用于设置表单默认值 */
  initData: Record<string, any> | null;
  /** 表单类型，用于区分不同的表单场景 */
  type?: string;
  /** 表单默认值，用于初始化表单字段 */
  defaultValues?: Record<string, any>;
  /** 额外的选项参数，传递给子组件使用 */
  options?: Record<string, any>;
}

// 组件别名，简化代码
const Step = Steps.Step;

function pickFieldErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  let msg: string | undefined;
  if (typeof err === 'string') {
    msg = err;
  } else if (Array.isArray(err)) {
    msg = err.find((item) => item?.message)?.message;
  } else if (typeof err === 'object' && 'message' in err && (err as { message?: string }).message) {
    msg = String((err as { message?: string }).message);
  }
  if (!msg) return undefined;
  // 忽略 Arco ValidateError 自身的英文壳文案
  if (msg.startsWith('form validate error')) return undefined;
  // 兼容 {{name}} 占位未替换的校验文案
  const normalized = msg.replace(/\{\{name\}\}/g, '').trim();
  return normalized || msg;
}

/** Arco await form.validate() 拒绝的是 ValidateError，真实字段错误在 error.errors */
function unwrapFormValidateErrors(caught?: unknown): Record<string, unknown> | undefined {
  if (!caught || typeof caught !== 'object') return undefined;
  const withErrors = caught as { errors?: unknown };
  if (withErrors.errors && typeof withErrors.errors === 'object' && !Array.isArray(withErrors.errors)) {
    return withErrors.errors as Record<string, unknown>;
  }
  // 兼容直接抛出字段错误对象（无 ValidateError 包装）
  if (!('errors' in caught) && !('message' in caught && 'name' in caught)) {
    return caught as Record<string, unknown>;
  }
  return undefined;
}

function hasResourceFieldValidateError(form: FormInstance, caught?: unknown): boolean {
  const caughtFieldErrors = unwrapFormValidateErrors(caught);
  if (caughtFieldErrors) {
    const hasInCaught = Object.keys(caughtFieldErrors).some((field) => field.startsWith('resources.') || field.startsWith('resources['));
    if (hasInCaught) return true;
  }
  return Object.keys(form.getFieldsError()).some((field) => field.startsWith('resources.') || field.startsWith('resources['));
}

function resolveStepValidateMessage(
  form: FormInstance,
  caught: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
  fieldOrder?: string[],
): string {
  // 提交过程中的运行时 Error（非 Arco 表单校验）应直接展示，避免误报「请填写必填项」
  if (caught instanceof Error && !String(caught.message || '').startsWith('form validate error')) {
    const msg = String(caught.message || '').trim();
    if (msg) return msg;
  }
  if (hasResourceFieldValidateError(form, caught)) {
    return t('data-center:sku.resource.validate.completeItems');
  }
  return getFirstFormValidateMessage(form, caught, t, fieldOrder) || t('components:please.fill.in.the.required.fields');
}

function formatResourceFieldError(field: string, message: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  const match = field.match(/^resources(?:\[(\d+)\]|\.(\d+))\./);
  if (!match) return message;
  const index = Number(match[1] ?? match[2]) + 1;
  return t('data-center:sku.resource.validate.rowHint', { index, detail: message });
}

/** 从表单字段错误中汇总去重文案（避免只露出按字母序最先的字段，如 CPU 盖住系统盘） */
export function getFirstFormValidateMessage(
  form: FormInstance,
  caught?: unknown,
  t?: (key: string, options?: Record<string, unknown>) => string,
  fieldOrder?: string[],
): string | undefined {
  const resourceFields: string[] = [];
  const otherFields: string[] = [];

  const collectFromObject = (obj: Record<string, unknown>) => {
    Object.keys(obj).forEach((field) => {
      // 跳过 Error / ValidateError 自身属性，避免把 errors/message/stack 当成字段名
      if (field === 'errors' || field === 'message' || field === 'stack' || field === 'name') return;
      if (field.startsWith('resources.') || field.startsWith('resources[')) {
        resourceFields.push(field);
      } else {
        otherFields.push(field);
      }
    });
  };

  const caughtFieldErrors = unwrapFormValidateErrors(caught);
  if (caughtFieldErrors) {
    collectFromObject(caughtFieldErrors);
  }

  const fieldErrors = form.getFieldsError();
  Object.keys(fieldErrors).forEach((field) => {
    if (field.startsWith('resources.') || field.startsWith('resources[')) {
      if (!resourceFields.includes(field)) resourceFields.push(field);
    } else if (!otherFields.includes(field)) {
      otherFields.push(field);
    }
  });

  const rank = new Map((fieldOrder || []).map((field, index) => [field, index]));
  const sortByPageOrder = (fields: string[]) =>
    [...fields].sort((a, b) => {
      const ra = rank.has(a) ? rank.get(a)! : Number.MAX_SAFE_INTEGER;
      const rb = rank.has(b) ? rank.get(b)! : Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      return a.localeCompare(b);
    });
  const orderedFields = fieldOrder?.length
    ? [...resourceFields.sort(), ...sortByPageOrder(otherFields)]
    : [...resourceFields.sort(), ...otherFields.sort()];
  const messages: string[] = [];

  for (const field of orderedFields) {
    const msg = pickFieldErrorMessage(fieldErrors[field] ?? caughtFieldErrors?.[field]);
    if (!msg) continue;
    const text = (field.startsWith('resources.') || field.startsWith('resources[')) && t ? formatResourceFieldError(field, msg, t) : msg;
    if (!messages.includes(text)) messages.push(text);
  }

  if (messages.length === 0) return undefined;
  return messages.join('；');
}

/**
 * 表单的默认配置属性
 */
const defaultFormProps = {
  /** 表单布局方式：垂直布局 */
  layout: 'vertical' as const,
  /** 表单样式：固定宽度，居中显示 */
  style: { minWidth: 700, maxWidth: 1200, width: '90%' },
  /** 自动完成：关闭浏览器自动完成功能 */
  autoComplete: 'off' as const,
};

/** 合并 Form 配置：后者覆盖前者，style 对象逐层合并 */
function mergeFormProps(...parts: (Record<string, any> | undefined)[]): Record<string, any> {
  return parts.reduce<Record<string, any>>((acc, part) => {
    if (!part) return acc;
    const merged = { ...acc, ...part };
    if (acc.style || part.style) {
      merged.style = { ...acc.style, ...part.style };
    }
    return merged;
  }, {});
}

/**
 * 步骤表单组件
 * 支持多步骤表单填写，包含步骤导航、表单验证、数据提交等功能
 */
export default function StepForm({
  title,
  cmpConfigs,
  type,
  showStep = true,
  defaultValues,
  options,
  expandOptions,
  formProps = {},
  form: externalForm,
  loading = false,
  breadcrumb = [],
  onSubmit,
  submitText,
  extraButtons,
  wrapSubmitButton,
  onCancel,
  onNext,
  onPrevious,
}: StepFormProps) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(1);
  const navigate = usePathTo();
  const [innerForm] = Form.useForm();
  const form = externalForm || innerForm;
  const [initData, setInitData] = useState<Record<string, any> | null>(null);
  const [loading1, setLoading1] = useState(false);

  // 默认验证规则
  const defaultRules = useMemo(
    () => [
      {
        required: true,
        message: t('common:required.field'),
      },
    ],
    [],
  );

  // 公共属性
  const commonProps = useMemo<CommonProps>(
    () => ({
      form,
      defaultRules,
      initData,
      type,
      options,
      ...(expandOptions || {}),
    }),
    [form, defaultRules, initData, type, options, expandOptions],
  );

  // 过滤显示和隐藏的配置
  const { hiddenConfigs, visibleConfigs } = useMemo(
    () => ({
      hiddenConfigs: cmpConfigs?.filter((config) => config.hidden) || [],
      visibleConfigs: cmpConfigs?.filter((config) => !config.hidden) || [],
    }),
    [cmpConfigs],
  );

  const activeFormProps = useMemo(() => {
    const stepFormProps = visibleConfigs[current - 1]?.formProps;
    return mergeFormProps(defaultFormProps, formProps, stepFormProps);
  }, [current, formProps, visibleConfigs]);

  // 重置表单
  useEffect(() => {
    return () => {
      form?.resetFields();
    };
  }, [form]);

  // 设置默认值
  useEffect(() => {
    if (!defaultValues || Object.keys(defaultValues).length === 0) return;
    setInitData(defaultValues);
    form.setFieldsValue(defaultValues);
  }, [defaultValues, form]);

  // 下一步
  const handleNext = useCallback(async () => {
    try {
      setLoading1(true);
      let values: Record<string, any>;
      try {
        values = await form.validate();
      } catch (error) {
        options?.onValidateFailed?.(error);
        Message.error(resolveStepValidateMessage(form, error, t, options?.validateFieldOrder));
        return;
      }
      options?.onValidateSuccess?.();
      try {
        onNext && (await onNext(current + 1, values));
        setCurrent((curr) => curr + 1);
      } catch (error) {
        // 接口/业务错误由请求层或业务侧提示，避免再误报「请填写必填项」
        console.log(error);
      }
    } finally {
      setLoading1(false);
    }
  }, [form, onNext, current, t, options]);

  // 提交
  const handleSubmit = useCallback(async () => {
    try {
      setLoading1(true);
      try {
        await form.validate();
      } catch (error) {
        options?.onValidateFailed?.(error);
        Message.error(resolveStepValidateMessage(form, error, t, options?.validateFieldOrder));
        return;
      }
      // 多步骤表单中后挂载字段可能未包含在 validate 返回值，需从表单实例读取完整值
      const values = form.getFieldsValue();
      try {
        await onSubmit(values);
      } catch (error) {
        // 接口错误由请求层统一提示，不再走表单校验兜底文案
        console.log(error);
      }
    } finally {
      setLoading1(false);
    }
  }, [form, onSubmit, t, options]);

  /** 额外按钮点击：可选校验后回调 */
  const handleExtraButtonClick = useCallback(
    async (btn: StepFormExtraButton) => {
      try {
        setLoading1(true);
        if (btn.validate !== false) {
          try {
            await form.validate();
          } catch (error) {
            options?.onValidateFailed?.(error);
            Message.error(resolveStepValidateMessage(form, error, t, options?.validateFieldOrder));
            return;
          }
        }
        const values = form.getFieldsValue();
        try {
          await btn.onClick(values);
        } catch (error) {
          console.log(error);
        }
      } finally {
        setLoading1(false);
      }
    },
    [form, options, t],
  );

  // 取消
  const handleCancel = useCallback(() => {
    if (typeof onCancel === 'function') {
      onCancel();
    } else {
      navigate(-1);
    }
  }, [onCancel, navigate]);

  // 上一步
  const handlePrevious = useCallback(() => {
    onPrevious && onPrevious(current - 1);
    setCurrent((curr) => curr - 1);
  }, [current, onPrevious]);

  const isLastStep = current === visibleConfigs.length;

  const visibleExtraButtons = useMemo(() => {
    if (!extraButtons?.length) return [];
    const ctx = { current, total: visibleConfigs.length, type };
    return extraButtons.filter((btn) => {
      if (typeof btn.visible === 'function') return btn.visible(ctx);
      if (typeof btn.visible === 'boolean') return btn.visible;
      // 默认仅最后一步展示
      return isLastStep;
    });
  }, [extraButtons, current, visibleConfigs.length, type, isLastStep]);

  // 渲染隐藏的表单项
  const renderHiddenFormItems = () => (
    <>
      {hiddenConfigs.map((item, index) => (
        <Form.Item key={`hidden-${index}`} hidden style={{ margin: 0 }}>
          <item.ele {...commonProps} />
        </Form.Item>
      ))}
    </>
  );

  // 渲染可见的表单项
  const renderVisibleFormItems = () => (
    <>
      {visibleConfigs
        .filter((_, idx) => idx < current)
        .map((item, index) => (
          <Form.Item key={`visible-${index}`} hidden={item.hidden || current !== index + 1} style={{ margin: '0 auto' }}>
            <item.ele {...commonProps} />
          </Form.Item>
        ))}
    </>
  );

  // 渲染步骤导航
  const renderSteps = () => {
    if (!showStep || visibleConfigs.length < 2) return null;

    return (
      <Steps current={current} style={{ margin: '20px auto' }} size="small">
        {visibleConfigs.map((item, index) => (
          <Step style={{ minWidth: 150 }} key={index} title={item.title} />
        ))}
      </Steps>
    );
  };

  // 渲染操作按钮
  const renderActionButtons = () => {
    const submitButton = (
      <Button type="primary" onClick={handleSubmit} loading={loading1}>
        {submitText ?? t('common:confirm')}
      </Button>
    );

    return (
      <div className={styles.flex_r} style={{ gap: 10, margin: 12 }}>
        <Button onClick={handleCancel} disabled={loading1}>
          {t('common:cancel')}
        </Button>
        {current > 1 && (
          <Button type="primary" onClick={handlePrevious} disabled={loading1}>
            {t('cluster:pre')}
          </Button>
        )}
        {visibleExtraButtons.map((btn) => {
          const button = (
            <Button
              key={btn.key}
              type={btn.type || 'default'}
              status={btn.status}
              disabled={loading1}
              loading={loading1}
              onClick={() => handleExtraButtonClick(btn)}
            >
              {btn.text}
            </Button>
          );
          return <React.Fragment key={btn.key}>{btn.wrapper ? btn.wrapper(button) : button}</React.Fragment>;
        })}
        {isLastStep ? (
          wrapSubmitButton ? (
            wrapSubmitButton(submitButton)
          ) : (
            submitButton
          )
        ) : (
          <Button type="primary" onClick={handleNext} loading={loading1}>
            {t('cluster:next')}
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <BreadcrumbNav items={breadcrumb} />
      <div className={styles.detail_head}>{title}</div>
      <Spin loading={loading} block className={styles.spin_container}>
        <div className={`${styles.card} ${styles.flex_r_c}`} style={{ minHeight: 600, gap: 16 }}>
          {renderSteps()}

          <Form form={form} {...activeFormProps}>
            {renderHiddenFormItems()}
            {renderVisibleFormItems()}
          </Form>

          {renderActionButtons()}
        </div>
      </Spin>
    </>
  );
}
